// src/pages/InstructorReviewPage.jsx

import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  EuiPage,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiButton,
  EuiSpacer,
  EuiCallOut,
  EuiListGroup,
  EuiListGroupItem,
  EuiLoadingSpinner,
  EuiHorizontalRule,
  EuiTitle,
  EuiButtonIcon,
  EuiFieldSearch,
  EuiModal,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiModalBody,
  EuiModalFooter,
  EuiSelect,
} from "@elastic/eui";
import { useParams } from "react-router-dom";
import { UserContext } from "../Context";
import Blocked from "../components/Blocked";
import { PostOptions } from "../utils/RequestUtils";
import { getApiUrl } from "../utils/api";
import AgentText from "../components/AgentText";
import UserText from "../components/UserText";
import "../css/ChatPage.css";

const InstructorReviewPageContent = () => {
  const { courseNumber, courseName } = useParams();

  // -------------------- Loading States --------------------
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  const [isLoadingChat, setIsLoadingChat] = useState(false);

  // -------------------- Data Structures --------------------
  const [studentList, setStudentList] = useState([]); // array of { username, email, full_name }
  const [sessionsMap, setSessionsMap] = useState({}); // { username: [sessionObj, ...] }

  // -------------------- Selected Items --------------------
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);

  // -------------------- Chat Data --------------------
  const [chatHistory, setChatHistory] = useState([]);
  const [chatPage, setChatPage] = useState(1);
  const [chatTotal, setChatTotal] = useState(0);
  const chatLimit = 20;

  // -------------------- Search & Sorting --------------------
  const [searchTerm, setSearchTerm] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  // -------------------- Config Data (Optional) --------------------
  const [selectedConfig, setSelectedConfig] = useState("");

  // -------------------- UI State for Sessions --------------------
  // When true, we show the horizontal list of sessions; when false, we collapse it.
  const [showSessions, setShowSessions] = useState(true);
  const [isAnalyzingSummary, setIsAnalyzingSummary] = useState(false);
  const [isAnalyzingChallenges, setIsAnalyzingChallenges] = useState(false);
  const [summaryResult, setSummaryResult] = useState(null);
  const [challengesResult, setChallengesResult] = useState(null);
  const [modalType, setModalType] = useState(null); // 'summary' or 'challenges'

  const [isModalVisible, setIsModalVisible] = useState(false);

  // -------------------- Fetch Configs on Mount --------------------
  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const body = { course_name: courseName };
        const resp = await fetch(getApiUrl("/api/agent/get/all"), PostOptions(body));
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.message || "Error fetching configs");
        // If there's only one config, auto-select it:
        if (data.data.length === 1) {
          setSelectedConfig(data.data[0].config_name);
        }
      } catch (error) {
        console.error("Error fetching configs: ", error);
      }
    };
    fetchConfigs();
  }, [courseName]);

  // -------------------- Fetch All Students on Mount (Lazy Loading) --------------------
  // This only loads basic student info (email, name, username) - sessions are loaded on demand

  const fetchAllStudents = useCallback(async () => {
    setIsLoadingStudents(true);
    try {
      const body = { course_name: courseName };
      const resp = await fetch(getApiUrl("/api/course/students"), PostOptions(body));
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.message || "Error fetching students");
      }
      // Handle different possible response formats from the backend
      const students = data.students || data.data || [];
      setStudentList(students);
    } catch (error) {
      console.error("Error fetching students: ", error);
      setStudentList([]); // Set empty array on error to prevent undefined errors
    } finally {
      setIsLoadingStudents(false);
    }
  }, [courseName]);

  useEffect(() => {
    fetchAllStudents();
  }, [courseName]);

  // -------------------- Handle Student Selection --------------------
  const handleSelectStudent = async (studentUsername) => {
    setSelectedStudent(studentUsername);
    setSelectedSession(null);
    setChatHistory([]);
    setChatPage(1);
    setChatTotal(0);

    // Show the sessions again whenever a new student is selected
    setShowSessions(true);

    try {
      // Using body in PostOptions:
      const resp = await fetch(getApiUrl("/api/session/get/all_for_student"), {
        method: "POST",
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: studentUsername,
          course_name: courseName,
          course_number: courseNumber,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message || "Error fetching sessions");
      setSessionsMap((prev) => ({ ...prev, [studentUsername]: data }));
      if (data.length > 0) {
        handleSelectSession(data[0]);
      }
    } catch (error) {
      console.error("Error fetching sessions for student: ", error);
    }
  };

  // -------------------- Handle Session Selection & Fetch Chat --------------------
  const handleSelectSession = async (session) => {
    setSelectedSession(session);
    setChatHistory([]);
    setChatPage(1);
    setSummaryResult(null); // ADD THIS LINE
    setChallengesResult(null); // ADD THIS LINE
    setChatTotal(0);
    setIsLoadingChat(true);
    // Once selected, collapse the session list
    setShowSessions(true);

    try {
      const body = {
        session_owner: session.session_owner,
        session_id: session._id || session.session_name, // Use _id as primary identifier
        course_name: courseName,
        course_number: courseNumber,
        page: 1,
        limit: chatLimit,
      };
      const resp = await fetch(
        getApiUrl("/api/session/chat/paginated"),
        PostOptions(body)
      );
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.message || "Error fetching chat");
      }
      setChatHistory(data.data);
      setChatTotal(data.total || 0);
    } catch (error) {
      console.error("Error fetching chat: ", error);
    } finally {
      setIsLoadingChat(false);
    }
  };

  // -------------------- Load More Chat --------------------
  const loadMoreChat = async () => {
    if (!selectedSession) return;
    const nextPage = chatPage + 1;
    setIsLoadingChat(true);
    try {
      const body = {
        session_owner: selectedSession.session_owner,
        session_id: selectedSession._id || selectedSession.session_name, // Use _id as primary identifier
        course_name: courseName,
        course_number: courseNumber,
        page: nextPage,
        limit: chatLimit,
      };
      const resp = await fetch(
        getApiUrl("/api/session/chat/paginated"),
        PostOptions(body)
      );
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.message || "Error loading more chat");
      }
      setChatHistory((prev) => [...prev, ...data.data]);
      setChatPage(nextPage);
    } catch (error) {
      console.error("Error loading more chat: ", error);
    } finally {
      setIsLoadingChat(false);
    }
  };

  // -------------------- Filter & Sort Students --------------------
  const getFilteredAndSortedStudents = () => {
    let filtered = studentList;
    if (searchTerm.trim()) {
      const lowerTerm = searchTerm.toLowerCase();
      filtered = filtered.filter((s) =>
        s.username.toLowerCase().includes(lowerTerm) ||
        (s.email && s.email.toLowerCase().includes(lowerTerm)) ||
        (s.full_name && s.full_name.toLowerCase().includes(lowerTerm))
      );
    }
    filtered.sort((a, b) => {
      // Sort by full name if available, otherwise by username
      const valA = (a.full_name || a.username).toLowerCase();
      const valB = (b.full_name || b.username).toLowerCase();
      if (valA < valB) return sortDirection === "asc" ? -1 : 1;
      if (valA > valB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return filtered;
  };

  const handleGenerateSummary = async () => {
    if (!selectedSession || chatHistory.length === 0) return;

    setIsAnalyzingSummary(true);
    setSummaryResult(null);

    try {
      const body = {
        session_owner: selectedSession.session_owner,
        session_id: selectedSession._id || selectedSession.session_name, // Use _id as primary identifier
        course_name: courseName,
        course_number: courseNumber,
      };

      const resp = await fetch(getApiUrl("/api/session/summary"), PostOptions(body));
      const data = await resp.json();

      if (!resp.ok) {
        throw new Error(data.message || "Error generating summary");
      }

      setSummaryResult(data.summary);
      setModalType("summary");
      setIsModalVisible(true);
    } catch (error) {
      console.error("Error generating summary: ", error);
      setSummaryResult("Error: Unable to generate summary. Please try again.");
      setModalType("summary");
      setIsModalVisible(true);
    } finally {
      setIsAnalyzingSummary(false);
    }
  };

  const handleAnalyzeChallenges = async () => {
    if (!selectedSession || chatHistory.length === 0) return;

    setIsAnalyzingChallenges(true);
    setChallengesResult(null);

    try {
      const body = {
        session_owner: selectedSession.session_owner,
        session_id: selectedSession._id || selectedSession.session_name, // Use _id as primary identifier
        course_name: courseName,
        course_number: courseNumber,
      };

      const resp = await fetch(getApiUrl("/api/session/challenges"), PostOptions(body));
      const data = await resp.json();

      if (!resp.ok) {
        throw new Error(data.message || "Error analyzing challenges");
      }

      setChallengesResult(data.challenges);
      setModalType("challenges");
      setIsModalVisible(true);
    } catch (error) {
      console.error("Error analyzing challenges: ", error);
      setChallengesResult(
        "Error: Unable to analyze challenges. Please try again."
      );
      setModalType("challenges");
      setIsModalVisible(true);
    } finally {
      setIsAnalyzingChallenges(false);
    }
  };

  const filteredAndSortedStudents = getFilteredAndSortedStudents();

  // -------------------- Main Render --------------------
  return (
    <EuiPage restrictWidth="90%">
      <EuiFlexGroup style={{ width: "100%" }}>
        {/* LEFT PANEL: Student List (made narrower) */}
        <EuiFlexItem grow={false} style={{ width: "240px" }}>
          <EuiPanel
            paddingSize="s"
            style={{ height: "85vh", display: "flex", flexDirection: "column" }}
          >
            {/* Fixed header container */}
            <div style={{ flex: "0 0 auto" }}>
              <EuiTitle size="s">
                <h3>
                  Students in {courseNumber} {courseName}
                </h3>
              </EuiTitle>
              <EuiSpacer size="s" />
              <EuiFieldSearch
                placeholder="Search by name, email, or username..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                isClearable
                fullWidth
              />
              <EuiSpacer size="s" />
              <EuiFlexGroup
                justifyContent="spaceBetween"
                alignItems="center"
                gutterSize="s"
              >
                <EuiFlexItem grow={5}>
                  <EuiSelect
                    options={[{ value: "alphabetical", text: "Alphabetical" }]}
                    value="alphabetical"
                    fullWidth
                    disabled
                  />
                </EuiFlexItem>
                <EuiFlexItem grow={1}>
                  <EuiButtonIcon
                    iconType={sortDirection === "asc" ? "sortUp" : "sortDown"}
                    aria-label="Toggle sort direction"
                    onClick={() =>
                      setSortDirection((prev) =>
                        prev === "asc" ? "desc" : "asc"
                      )
                    }
                  />
                </EuiFlexItem>
              </EuiFlexGroup>
              <EuiSpacer size="s" />
              <EuiHorizontalRule margin="none" />
            </div>

            {/* Scrollable list container */}
            <div style={{ flex: "1 1 auto", overflowY: "auto" }}>
              {isLoadingStudents && <EuiLoadingSpinner size="l" />}
              {!isLoadingStudents && filteredAndSortedStudents.length === 0 && (
                <EuiCallOut
                  color="warning"
                  title="No matching students found."
                />
              )}
              <EuiListGroup flush bordered maxWidth={false}>
                {filteredAndSortedStudents.map((stu, index) => {
                  // Create a display label with name and email
                  const displayName = stu.full_name || stu.username;
                  const email = stu.email || stu.username;
                  const label = displayName !== email ? `${displayName} (${email})` : displayName;
                  
                  return (
                    <EuiListGroupItem
                      key={index}
                      label={label}
                      onClick={() => handleSelectStudent(stu.username)}
                      isActive={selectedStudent === stu.username}
                      size="s"
                    />
                  );
                })}
              </EuiListGroup>
            </div>
          </EuiPanel>
        </EuiFlexItem>

        {/* RIGHT PANEL: Sessions Bar (horizontal) above Chat */}
        <EuiFlexItem grow={1}>
          <EuiPanel
            paddingSize="s"
            style={{ height: "85vh", overflowY: "auto" }}
          >
            <EuiTitle size="s">
              <h3>Chat Review</h3>
            </EuiTitle>
            <EuiHorizontalRule margin="xs" />

            {/* Show horizontal sessions bar if a student is selected and sessions exist */}
            {selectedStudent &&
              sessionsMap[selectedStudent] &&
              sessionsMap[selectedStudent].length > 0 && (
                <>
                  {showSessions ? (
                    <EuiPanel paddingSize="s" style={{ marginBottom: "8px" }}>
                      <EuiFlexGroup wrap responsive={false} gutterSize="s">
                        {sessionsMap[selectedStudent].map((sessionObj, idx) => {
                          const keyStr = `${sessionObj.session_name}-${idx}`;
                          return (
                            <EuiFlexItem grow={false} key={keyStr}>
                              <EuiButton
                                onClick={() => handleSelectSession(sessionObj)}
                                fill={
                                  selectedSession &&
                                  sessionObj.session_name ===
                                    selectedSession.session_name
                                }
                              >
                                {sessionObj.session_name}
                              </EuiButton>
                            </EuiFlexItem>
                          );
                        })}
                      </EuiFlexGroup>
                    </EuiPanel>
                  ) : (
                    <EuiPanel paddingSize="s" style={{ marginBottom: "8px" }}>
                      <EuiButton onClick={() => setShowSessions(true)}>
                        {selectedSession
                          ? selectedSession.session_name
                          : "Select a Session"}
                      </EuiButton>
                    </EuiPanel>
                  )}
                </>
              )}

            {/* If no student selected or no sessions, show a callout */}
            {!selectedStudent && (
              <EuiCallOut color="primary" title="Please select a student">
                Sessions will appear here once a student is chosen.
              </EuiCallOut>
            )}
            {selectedStudent &&
              (!sessionsMap[selectedStudent] ||
                sessionsMap[selectedStudent].length === 0) && (
                <EuiCallOut
                  color="warning"
                  title="No sessions for this student."
                >
                  No sessions found.
                </EuiCallOut>
              )}

            <EuiSpacer size="s" />

            {/* CHAT AREA */}
            {selectedSession ? (
              <>
                <div
                  style={{
                    maxHeight: "65vh",
                    overflowY: "auto",
                    padding: "8px",
                  }}
                >
                  {isLoadingChat && <EuiLoadingSpinner size="l" />}
                  {chatHistory.map((chatItem, idx) =>
                    chatItem.type === "query" ? (
                      <div key={idx}>
                        <UserText
                          name={selectedSession.session_owner}
                          text={chatItem.text}
                        />
                        <EuiSpacer size="s" />
                      </div>
                    ) : (
                      <div key={idx}>
                        <AgentText
                          text={chatItem.text}
                          agentName="MerryQuery"
                        />
                        <EuiSpacer size="s" />
                      </div>
                    )
                  )}
                </div>
                {chatHistory.length < chatTotal && (
                  <>
                    <EuiButton onClick={loadMoreChat} isLoading={isLoadingChat}>
                      Load More Messages
                    </EuiButton>
                    <EuiSpacer size="s" />
                  </>
                )}

                {/* Analysis Buttons */}
                <EuiFlexGroup gutterSize="s">
                  <EuiFlexItem>
                    <EuiButton
                      onClick={handleGenerateSummary}
                      isLoading={isAnalyzingSummary}
                      disabled={
                        isAnalyzingSummary ||
                        isAnalyzingChallenges ||
                        chatHistory.length === 0
                      }
                      iconType="document"
                      fill
                    >
                      Generate Summary
                    </EuiButton>
                  </EuiFlexItem>
                  <EuiFlexItem>
                    <EuiButton
                      onClick={handleAnalyzeChallenges}
                      isLoading={isAnalyzingChallenges}
                      disabled={
                        isAnalyzingSummary ||
                        isAnalyzingChallenges ||
                        chatHistory.length === 0
                      }
                      iconType="alert"
                      color="warning"
                      fill
                    >
                      Areas of Challenge
                    </EuiButton>
                  </EuiFlexItem>
                </EuiFlexGroup>
              </>
            ) : (
              selectedStudent && (
                <EuiCallOut color="primary" title="No session selected">
                  Please select a session above to view chat details.
                </EuiCallOut>
              )
            )}
          </EuiPanel>
        </EuiFlexItem>
      </EuiFlexGroup>
      {/* Analysis Modal */}
      {isModalVisible && (
        <EuiModal onClose={() => setIsModalVisible(false)} maxWidth={800}>
          <EuiModalHeader>
            <EuiModalHeaderTitle>
              {modalType === "summary"
                ? "Session Summary"
                : "Areas of Challenge"}
            </EuiModalHeaderTitle>
          </EuiModalHeader>
          <EuiModalBody>
            <EuiCallOut
              color={modalType === "summary" ? "primary" : "warning"}
              iconType={modalType === "summary" ? "iInCircle" : "alert"}
            >
              <p style={{ whiteSpace: "pre-wrap" }}>
                {modalType === "summary" ? summaryResult : challengesResult}
              </p>
            </EuiCallOut>
          </EuiModalBody>
          <EuiModalFooter>
            <EuiButton onClick={() => setIsModalVisible(false)} fill>
              Close
            </EuiButton>
          </EuiModalFooter>
        </EuiModal>
      )}
    </EuiPage>
  );
};

// -------------------- Wrapper with UserContext Check --------------------
const InstructorReviewPage = () => {
  const { user } = useContext(UserContext);

  const hasAccess =
    user && (user.role === "teacher" || user.effectiveRole === "manager");

  if (!hasAccess) {
    return <Blocked name="Teacher Review Page" />;
  }

  return <InstructorReviewPageContent />;
};
export default InstructorReviewPage;
